import memoize from 'memoize-one'
import { pattern } from '@gsc/utils'
import { typedAction } from '@gsc/preact-redux'
import {
  Action,
  ActionSubmit,
  ActionType,
  Page,
  reduceBlocks,
  FormElement,
  findFormElements,
  BlockType,
} from '@gsc/types'
import { evaluate, Context } from '@gsc/conditions'
import { ElementData, FormData, FormState, Notification, TemplateTags, WidgetInstance } from '../types'
import type { RootState, AppThunk } from './store'

export interface WidgetsState {
  [id: string]: Readonly<WidgetInstance>
}

const initialState: WidgetsState = {}

export function setVisibility(id: number, show = true) {
  return typedAction('widgets/SET_VISIBILITY', { id, show })
}

export function setAnimating(id: number, animating = true) {
  return typedAction('widgets/SET_ANIMATING', { id, animating })
}

export function setNotification(id: number, notification: Notification) {
  return typedAction('widgets/SET_NOTIFICATION', { id, notification })
}

export function updateFormState(id: number, formState: Partial<FormState>) {
  return typedAction('widgets/UPDATE_FORM_STATE', { id, formState })
}

export function setFormFieldValue(id: number, fieldName: string, value: ElementData) {
  return typedAction('widgets/SET_FORM_FIELD_VALUE', { id, fieldName, value })
}

export function goToPrevPage(id: number) {
  return typedAction('widgets/GO_TO_PREV_PAGE', { id })
}

export function setPendingActions(
  id: number,
  params: {
    actions: Action[]
    initiator?: ActionSubmit
    origin?: string
  },
) {
  return typedAction('widgets/SET_PENDING_ACTIONS', { id, ...params })
}

// internal actions

function upsertWidget(widget: WidgetInstance) {
  return typedAction('widgets/UPSERT', widget)
}

function setDefaultFormData(id: number, data: FormData) {
  return typedAction('widgets/SET_DEFAULT_FORM_DATA', { id, data })
}

function goToPage(id: number, page: number) {
  return typedAction('widgets/GO_TO_PAGE', { id, page })
}

type WidgetsAction = ReturnType<
  | typeof upsertWidget
  | typeof setVisibility
  | typeof setAnimating
  | typeof setNotification
  | typeof setDefaultFormData
  | typeof updateFormState
  | typeof setFormFieldValue
  | typeof goToPrevPage
  | typeof goToPage
  | typeof setPendingActions
>

function widgetReducer(widget: WidgetInstance, action: WidgetsAction): WidgetInstance {
  switch (action.type) {
    case 'widgets/SET_VISIBILITY':
      return { ...widget, show: action.payload.show }

    case 'widgets/SET_ANIMATING':
      return { ...widget, animating: action.payload.animating }

    case 'widgets/SET_NOTIFICATION':
      return { ...widget, notification: action.payload.notification }

    case 'widgets/SET_DEFAULT_FORM_DATA': {
      const { data = {} } = action.payload
      if (Object.keys(data).length > 0) {
        return {
          ...widget,
          formState: {
            ...widget.formState,
            data: {
              ...data,
              ...widget.formState.data,
            },
          },
        }
      }

      return widget
    }

    case 'widgets/UPDATE_FORM_STATE': {
      const { formState } = action.payload
      const data = formState.data
        ? Object.keys(formState.data).reduce(
            (res, key) => {
              res[key] = { ...res[key], ...formState.data[key] }
              return res
            },
            { ...widget.formState.data },
          )
        : widget.formState.data

      return {
        ...widget,
        formState: {
          ...widget.formState,
          ...formState,
          data,
        },
      }
    }

    case 'widgets/SET_FORM_FIELD_VALUE':
      return {
        ...widget,
        formState: {
          ...widget.formState,
          data: {
            ...widget.formState.data,
            [action.payload.fieldName]: action.payload.value,
          },
          pristine: false,
        },
      }

    case 'widgets/GO_TO_PREV_PAGE':
      if (widget.pageStack.length > 0) {
        const pageStack = [...widget.pageStack]
        const prevPage = Math.max(pageStack.pop(), 0)
        return {
          ...widget,
          activePage: prevPage,
          pageStack,
        }
      } else if (PREVIEW && widget.activePage > 0) {
        return {
          ...widget,
          activePage: widget.activePage - 1,
        }
      }

      return widget

    case 'widgets/GO_TO_PAGE': {
      const { page } = action.payload
      return widget.activePage !== page
        ? {
            ...widget,
            activePage: page,
            pageStack:
              typeof widget.activePage === 'undefined' ? widget.pageStack : [...widget.pageStack, widget.activePage],
          }
        : widget
    }

    case 'widgets/SET_PENDING_ACTIONS': {
      const { actions, initiator, origin } = action.payload
      return {
        ...widget,
        pendingActions: { actions, initiator, origin },
      }
    }

    default:
      return widget
  }
}

export default function reducer(state = initialState, action: WidgetsAction): WidgetsState {
  if (action.type === 'widgets/UPSERT') {
    return {
      ...state,
      [action.payload.id]: action.payload,
    }
  }

  const current = action.payload?.id && state[action.payload.id]

  if (current) {
    const updated = widgetReducer(current, action)
    if (current !== updated) {
      return {
        ...state,
        [action.payload.id]: updated,
      }
    }
  }

  return state
}

// selectors

function getFormElementTag(el: FormElement, data: ElementData): string {
  const value = data?.value || []
  switch (el.type) {
    case BlockType.CheckboxGroup:
    case BlockType.RadioGroup:
      return el.options
        .filter(i => value.includes(i.value))
        .map(i => i.label)
        .join()

    case BlockType.Select:
      return el.options
        .filter(opt => value.indexOf(opt.value) !== -1)
        .map(opt => opt.label)
        .join()

    case BlockType.Flag:
      return el.label

    default:
      return value.join()
  }
}

function selectIntrinsicWidgetTemplateTags({ id, name }: WidgetInstance): TemplateTags {
  return { $widgetId: `${id}`, $widgetName: name }
}

function selectWidgetTemplateTags(widget: WidgetInstance): TemplateTags {
  const { pages, formState } = widget
  const allFormElements = pages.flatMap(p => findFormElements(p.root))
  const tags = allFormElements.reduce<{ [key: string]: string }>((res, elem) => {
    const tag = getFormElementTag(elem, formState.data[elem.name])
    res[elem.name] = tag != '' ? tag : undefined
    return res
  }, {})

  return { ...tags, ...selectIntrinsicWidgetTemplateTags(widget) }
}

export function selectWidgetById(state: RootState, id: number): WidgetInstance {
  return state.widgets[id]
}

const selectWidgetTemplateTagsMemoized = memoize(selectWidgetTemplateTags)

const mergeTemplateTags = (commonTags: TemplateTags, widgetTags: TemplateTags): TemplateTags => {
  return { ...commonTags, ...widgetTags }
}

const mergeTemplateTagsMemoized = memoize(mergeTemplateTags)

export function selectTemplateTagsForWidget(state: RootState, widgetId: number): TemplateTags {
  const { templateTags, widgets } = state
  return mergeTemplateTagsMemoized(templateTags, selectWidgetTemplateTagsMemoized(widgets[widgetId]))
}

function formatFormElementValue(el: FormElement, data: ElementData): string | string[] | boolean | undefined {
  const value = data?.value || []

  if ([BlockType.Flag].includes(el.type)) {
    return value.length > 0
  }

  const isMultiple = [BlockType.CheckboxGroup, BlockType.RadioGroup, BlockType.Select].includes(el.type)
  return isMultiple ? value : value[0] || null
}

function selectWidgetFormEvaluateContext({ pages, formState }: WidgetInstance): Context {
  const allFormElements = pages.flatMap(p => findFormElements(p.root))
  const $form = allFormElements.reduce<Context>((res, elem) => {
    res[elem.name] = formatFormElementValue(elem, formState.data[elem.name])
    return res
  }, {})
  return { $form }
}

function selectEvaluateContextForWidget(state: RootState, widgetId: number): Context {
  const { templateTags, widgets } = state
  return { ...templateTags, ...selectWidgetFormEvaluateContext(widgets[widgetId]) }
}

// thunks

function getDefaultElementValue(el: FormElement, templateTags: TemplateTags): ElementData | null {
  switch (el.type) {
    case BlockType.CheckboxGroup:
    case BlockType.RadioGroup:
      const value = el.options.filter(i => i.defaultChecked).map(i => i.value)
      return value.length > 0 ? { value } : null

    case BlockType.Select: {
      const opt = el.options.find(opt => opt.defaultChecked)
      return opt ? { value: [opt.value] } : null
    }

    case BlockType.Input:
    case BlockType.Textarea:
    case BlockType.Hidden: {
      return el.defaultValue != null ? { value: [pattern(el.defaultValue, templateTags)] } : null
    }

    case BlockType.Flag: {
      return el.defaultChecked ? { value: [el.value || el.name || 'on'] } : null
    }
  }
}

function createDefaultFormData(page: Page, templateTags: TemplateTags): FormData {
  return findFormElements(page.root).reduce<FormData>((res, f) => {
    const defaultValue = getDefaultElementValue(f, templateTags)
    if (defaultValue) {
      res[f.name] = defaultValue
    }
    return res
  }, {})
}

export const createOrUpdateWidget =
  (widget: WidgetInstance): AppThunk =>
  (dispatch, getState) => {
    const { id, activePage, pages } = widget
    const templateTags = { ...getState().templateTags, ...selectIntrinsicWidgetTemplateTags(widget) }
    const defaultFormData = createDefaultFormData(pages[activePage], templateTags)
    dispatch(upsertWidget({ ...widget, show: true }))
    dispatch(setDefaultFormData(id, defaultFormData))
  }

export const resetWidget =
  (id: number): AppThunk =>
  (dispatch, getState) => {
    const widget = selectWidgetById(getState(), id)
    const templateTags = { ...getState().templateTags, ...selectIntrinsicWidgetTemplateTags(widget) }
    const data = createDefaultFormData(widget.pages[0], templateTags)
    dispatch(
      upsertWidget({
        ...widget,
        activePage: 0,
        pageStack: [],
        formState: { data },
      }),
    )
  }

function pageHasSubmit({ root }: Page): boolean {
  return reduceBlocks<Action[]>(root, b => ('actions' in b && b.actions) || [])
    .flat()
    .some(a => a.type === ActionType.Submit)
}

export const goToNextPage =
  (id: number): AppThunk<{ shouldSendData: boolean; pageChanged: boolean }> =>
  (dispatch, getState) => {
    const { activePage, pages, pendingActions: { initiator } = {} } = selectWidgetById(getState(), id)
    let nextPage: number

    if (initiator?.branchLogic) {
      const context = selectEvaluateContextForWidget(getState(), id)
      const rule = initiator.branchLogic.find(r => !r.condition || evaluate(r.condition, context))
      if (!rule) {
        console.log('No matching rule found for branch logic')
        return { shouldSendData: false, pageChanged: false }
      }

      if (!rule.to) {
        return { shouldSendData: true, pageChanged: false } // finish
      }

      nextPage = pages.findIndex(p => p.id === rule.to)
      if (nextPage === -1) {
        console.log(`Page with id=${rule.to} not found`)
        return { shouldSendData: false, pageChanged: false }
      }
    } else {
      nextPage = Math.min(activePage + 1, pages.length - 1)
    }

    const pageChanged = nextPage !== activePage

    if (pageChanged) {
      const templateTags = selectTemplateTagsForWidget(getState(), id)
      const defaultFormData = createDefaultFormData(pages[nextPage], templateTags)
      dispatch(setDefaultFormData(id, defaultFormData))
      dispatch(goToPage(id, nextPage))
    }

    return {
      shouldSendData: !pageChanged || !pageHasSubmit(pages[nextPage]),
      pageChanged,
    }
  }
